// W1-6 — THE BOOKMARK BAR, shown in BOTH readers: name and save the current page, and jump to any
// existing bookmark for this book. `page` is 1-indexed as displayed. The type, the prompt, the
// placeholder, the routes, the counting and — as of R61 — both WRITES all come from
// `src/lib/bookmarks.ts`, so a gate can RUN them.
//
// R61 MOVED IT OUT OF App.tsx for the reason this line keeps finding: **the thing that moved is the
// thing that needed an instrument.** Every law about a bookmark was already extracted and held
// except the one that decides whether her mark exists at all — the save was
// `await fetch(...).catch(() => {})` with the form closing on the next line, so a refused bookmark
// closed the form, cleared her name and left nothing behind. It holds three pieces of state, so no
// gate can CALL it (R10-3's ruling: a stateful unit moves anyway when the law it carries needs one).
import { useEffect, useState, type FormEvent } from "react";
import { Bookmark } from "lucide-react";
import { t } from "../strings.js";
import {
  bookmarkNamePlaceholder, listBookmarks, saveBookmark, bookmarkNote, type Bookmark as SavedBookmark,
} from "../lib/bookmarks";

export default function BookmarkControls({ bookSlug, bookTitle, page, onJump, language }: {
  bookSlug: string;
  bookTitle: string;
  page: number;
  onJump: (page: number) => void;
  language: string;
}) {
  const [marks, setMarks] = useState<SavedBookmark[]>([]);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");   // R61: the one line this bar says when a save did not go

  const load = () => listBookmarks<SavedBookmark>(bookSlug).then((rows) => { if (rows) setMarks(rows); });
  useEffect(() => { load(); }, [bookSlug]);

  // **THE ANSWER IS READ AND THE FORM ONLY CLOSES ON A SAVE THAT LANDED** (R52: held by POSITION,
  // so no later spelling can put the reset back above the answer). Her typed name survives a
  // refusal, because retyping it is the one thing she should never have to do twice.
  const save = async (e: FormEvent) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    setNote("");
    const answer = await saveBookmark({ bookSlug, bookTitle, page, name: n });
    if (!answer.ok) {
      setNote(bookmarkNote(answer, { signIn: t(language, "bookmark.signIn"), failed: t(language, "bookmark.saveFailed") }));
      return;
    }
    setName("");
    setNaming(false);
    load();
  };

  return (
    <div className="bookmark-bar">
      {naming ? (
        <form className="bookmark-bar__form" onSubmit={save}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={bookmarkNamePlaceholder(page, t(language, "bookmark.namePlaceholder"))} autoFocus maxLength={120} />
          <button type="submit" className="bookmark-bar__save">{t(language, "common.save")}</button>
          <button type="button" className="bookmark-bar__cancel" onClick={() => { setNaming(false); setName(""); setNote(""); }}>{t(language, "common.cancel")}</button>
        </form>
      ) : (
        <button type="button" className="bookmark-bar__add" onClick={() => setNaming(true)}>
          <Bookmark size={14} /> {t(language, "bookmark.prompt")}
        </button>
      )}
      {note ? <p className="bookmark-bar__note" role="status">{note}</p> : null}
      {marks.length ? (
        <div className="bookmark-bar__list">
          {marks.map((m) => (
            <button key={m.id} type="button" className="bookmark-chip" onClick={() => onJump(m.page)} title={t(language, "bookmark.goToPage", { page: m.page })}>
              {m.name} <span className="bookmark-chip__page">p.{m.page}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
